import { useEffect, useState } from 'react'
import { Activity, AtSign, Camera, Loader2, ShieldCheck } from 'lucide-react'
import { Select, adminMutate, apiGet, usePermissions, useToast } from '../admin/primitives.js'
import { Field, TextInput, NumberInput, SwitchRow } from '../admin/form.js'
import { useSaveBar } from '../shell/editing.js'

/**
 * PROVIDER INSTAGRAM — écran de réglages (§14, §19-20, §42).
 *
 * L'écran a une obligation : ne jamais laisser croire qu'on collecte plus
 * qu'on ne collecte. La matrice de capacités est affichée telle qu'elle est,
 * y compris — surtout — la ligne qui dit que les Stories concurrentes ne
 * sont accessibles par aucune voie officielle.
 *
 * Le jeton n'est jamais relu depuis le serveur : on n'affiche que sa
 * présence, et le champ de saisie repart vide après enregistrement.
 */

type CapState = 'SUPPORTED' | 'PARTIAL' | 'NOT_SUPPORTED' | 'REQUIRES_ACCOUNT_AUTHORIZATION' | 'UNKNOWN'

interface ProviderView {
  config: {
    backend_kind: 'NONE' | 'AUTHORIZED_API'
    backend_settings: { graph_version?: string; ig_user_id?: string; lookback_days?: number }
    enabled: boolean
    status: string
    has_credentials: boolean
    last_check_at: string | null
    last_success_at: string | null
    last_error: string | null
    max_requests_per_hour: number
    max_requests_per_day: number
    retention_days: number
    cost_per_request_millicents: number
  }
  capabilities: Record<string, CapState>
  notes: Record<string, string>
  usage: {
    requests_hour: number; requests_day: number; requests_month: number
    items_month: number; errors_day: number
    cost_month_millicents: number; cost_day_millicents: number
  }
  backend_name: string
}

const CAP_ORDER = ['PROFILE', 'POSTS', 'REELS', 'STORIES', 'HIGHLIGHTS', 'HISTORICAL_POSTS', 'IMAGE_TEXT'] as const
const CAP_LABEL: Record<string, string> = {
  PROFILE: 'Profil', POSTS: 'Publications', REELS: 'Reels', STORIES: 'Stories',
  HIGHLIGHTS: 'À la une', HISTORICAL_POSTS: 'Historique', IMAGE_TEXT: 'Texte dans les images',
}
const CAP_TEXT: Record<CapState, string> = {
  SUPPORTED: 'disponible',
  PARTIAL: 'partiel',
  NOT_SUPPORTED: 'non disponible',
  REQUIRES_ACCOUNT_AUTHORIZATION: 'nécessite l’accord du compte',
  UNKNOWN: 'inconnu',
}
const STATUS_LABEL: Record<string, string> = {
  NOT_CONFIGURED: 'Non configuré', READY: 'Prêt', HEALTHY: 'Actif',
  DEGRADED: 'Dégradé', RATE_LIMITED: 'Quota atteint',
  UNAVAILABLE: 'Indisponible', DISABLED: 'Désactivé',
}
const BACKEND_OPTIONS = [
  { value: 'NONE', label: 'Aucun — compte référencé, pas collecté' },
  { value: 'AUTHORIZED_API', label: 'API officielle Meta (business_discovery)' },
]

function euros(millicents: number): string {
  return (millicents / 100000).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })
}
function quandFr(iso: string | null): string {
  if (!iso) return 'jamais'
  return new Date(iso).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris',
  })
}

/**
 * STORIES INSTAGRAM — LE FOURNISSEUR, ET CE QU'IL COÛTE.
 *
 * Les Stories d'un concurrent ne sont accessibles par AUCUNE voie officielle
 * Meta. Elles passent donc par un fournisseur tiers, facturé à l'appel.
 *
 * C'est le SEUL écran où sa marque apparaît : partout ailleurs, Gestion ne
 * parle que de « Stories Instagram ». Le jour où le fournisseur change, seul
 * ce panneau bouge.
 *
 * Ce navigateur n'appelle jamais le fournisseur. Il lit un état que le
 * worker a écrit — le seul processus qui ait le droit de le joindre.
 */
interface StoriesView {
  enabled: boolean
  provider: string
  provider_label: string
  base_url: string | null
  has_credentials: boolean
  status: string
  last_call_at: string | null
  last_success_at: string | null
  last_error: string | null
  last_latency_ms: number | null
  credits_remaining: number | null
  credits_last: number | null
  last_found: number | null
  retention_days: number
  venues_enabled: number
  max_calls_per_day: number
  calls_last_24h: number
}

const STORIES_STATUS_FR: Record<string, string> = {
  DISABLED: 'Collecte désactivée',
  NOT_CONFIGURED: 'Clé du fournisseur à renseigner',
  READY: 'Configuré — aucune collecte encore effectuée',
  HEALTHY: 'Opérationnel',
  DEGRADED: 'Partiellement dégradé',
  RATE_LIMITED: 'Le fournisseur demande de patienter',
  UNAVAILABLE: 'Fournisseur indisponible',
}

function StoriesProviderPanel() {
  const permissions = usePermissions()
  const peutGerer = permissions.permissions.includes('soiree.manage')

  const [v, setV] = useState<StoriesView | null>(null)
  const [enabled, setEnabled] = useState(false)
  const [apiKey, setApiKey] = useState('')
  const [plafond, setPlafond] = useState(20)

  const charger = async () => {
    const d = await apiGet<StoriesView>('/api/v1/market/social/stories').catch(() => null)
    if (!d) return
    setV(d)
    setEnabled(d.enabled)
    setPlafond(d.max_calls_per_day)
  }
  useEffect(() => { void charger() }, [])

  // Écart RÉEL avec l'état serveur — une clé saisie compte, une clé vide non.
  const dirty = v !== null && (
    enabled !== v.enabled || plafond !== v.max_calls_per_day || apiKey.trim() !== ''
  )

  useSaveBar({
    dirty,
    saveLabel: 'Enregistrer les Stories Instagram',
    onSave: async () => {
      const body: Record<string, unknown> = { enabled, max_calls_per_day: plafond }
      // La clé n'est envoyée que si l'utilisateur en a saisi une : un champ
      // laissé vide ne doit JAMAIS effacer celle qui est déjà au coffre.
      if (apiKey.trim()) body['api_key'] = apiKey.trim()
      const r = await adminMutate<{ error?: string }>('PUT', '/api/v1/market/social/stories', body)
      if (r.status !== 204 && r.status !== 200) return 'Enregistrement impossible.'
      setApiKey('')
      await charger()
      return true
    },
    onCancel: () => {
      if (!v) return
      setEnabled(v.enabled)
      setPlafond(v.max_calls_per_day)
      setApiKey('')
    },
    successMessage: 'Configuration des Stories enregistrée.',
  })

  const ok = v?.status === 'HEALTHY'
  const attention = v?.status === 'DEGRADED' || v?.status === 'RATE_LIMITED'
    || v?.status === 'UNAVAILABLE'

  return (
    // Ce panneau portait un titre de niveau 3 dans une carte sans en-tête : il
    // ne ressemblait à aucun de ses voisins. Il adopte la structure commune —
    // en-tête, titre de section, corps — comme « Familles ».
    <section className="lc-panel gd-panel" aria-labelledby="set-stories">
      <header className="lc-panel__head">
        <h2 className="lc-section-title" id="set-stories">
          <Camera size={16} aria-hidden="true" /> Stories Instagram
        </h2>
      </header>
      <div className="lc-panel__body lc-stack">

      <SwitchRow
        checked={enabled}
        onChange={setEnabled}
        disabled={!peutGerer}
        title="Collecter les Stories des concurrents"
        desc="Les Stories publiques des établissements surveillés sont récupérées, puis
              lues localement sur le serveur. Aucune image ne part vers un service d’IA externe."
      />

      {v ? (
        <dl className="lc-kv lc-kv--blocs">
          <div>
            <dt>État</dt>
            <dd>
              <span className={`lc-dot ${ok ? 'lc-dot--ok' : attention ? 'lc-dot--warning' : ''}`} aria-hidden="true" />
              {' '}{STORIES_STATUS_FR[v.status] ?? v.status}
            </dd>
          </div>
          <div><dt>Fournisseur</dt><dd>{v.provider_label}</dd></div>
          <div><dt>Clé d’accès</dt><dd>{v.has_credentials ? 'enregistrée au coffre' : 'absente'}</dd></div>
          <div><dt>Établissements collectés</dt><dd>{v.venues_enabled}</dd></div>
          <div><dt>Dernier appel</dt><dd>{quandFr(v.last_call_at)}</dd></div>
          <div><dt>Dernier succès</dt><dd>{quandFr(v.last_success_at)}</dd></div>
          {/* §43 — jamais codé en dur : ces chiffres viennent de la réponse
              du fournisseur, et disent ce qu'il reste à dépenser. */}
          <div><dt>Temps de la dernière requête</dt>
            <dd>{v.last_latency_ms !== null ? `${(v.last_latency_ms / 1000).toFixed(1)} s` : '—'}</dd></div>
          <div><dt>Coût de la dernière requête</dt>
            <dd>{v.credits_last !== null ? `${v.credits_last} crédit${v.credits_last > 1 ? 's' : ''}` : '—'}</dd></div>
          <div><dt>Crédits restants</dt>
            <dd>{v.credits_remaining !== null ? v.credits_remaining : '—'}</dd></div>
          <div><dt>Stories au dernier relevé</dt>
            <dd>{v.last_found !== null ? v.last_found : '—'}</dd></div>
          <div><dt>Conservation des captures</dt><dd>{v.retention_days} jours</dd></div>
          <div><dt>Relevés effectués (24 h)</dt>
            <dd>{v.calls_last_24h} / {v.max_calls_per_day === 0 ? '∞' : v.max_calls_per_day}</dd></div>
        </dl>
      ) : null}

      {v?.last_error ? <p className="lc-hint lc-hint--warn">{v.last_error}</p> : null}

      <Field label="Clé d’accès du fournisseur"
        helper={v?.has_credentials
          ? 'Une clé est déjà enregistrée. Laissez vide pour la conserver.'
          : 'La clé est chiffrée au coffre et n’est jamais réaffichée.'}>
        <TextInput value={apiKey} onChange={setApiKey} maxLength={500}
          placeholder={v?.has_credentials ? '•••••••• (inchangée)' : 'Coller la clé'}
          ariaLabel="Clé d’accès du fournisseur de Stories" disabled={!peutGerer} />
      </Field>

      <Field label="Relevés autorisés par 24 h"
        helper={plafond === 0
          ? '0 = aucune limite. Le fournisseur sera sollicité autant de fois que la veille le demande.'
          : `Le fournisseur est facturé à l’appel. Au-delà de ${plafond} relevés sur 24 h glissantes,`
            + ' la collecte attend — les autres sources de veille continuent normalement.'}>
        {/* Sans droit de gestion, le chiffre se lit mais ne s'édite pas :
            un champ modifiable qu'on ne peut pas enregistrer est un piège. */}
        {peutGerer ? (
          <NumberInput value={plafond} min={0} max={500} unit="relevés"
            onChange={(raw) => setPlafond(Math.max(0, Math.min(500, Number(raw) || 0)))}
            ariaLabel="Relevés autorisés par 24 h" />
        ) : (
          <p className="lc-kv__val">{plafond === 0 ? 'aucune limite' : `${plafond} relevés`}</p>
        )}
      </Field>

      <p className="lc-hint">
        La collecte s’active ensuite <strong>établissement par établissement</strong>, dans
        la fiche de chacun. Lire une affiche mobilise le serveur pendant plusieurs
        minutes : tous les comptes surveillés n’ont pas à l’être.
      </p>
      </div>
    </section>
  )
}

interface JetonMeta {
  etat: 'ABSENT' | 'VALIDE' | 'BIENTOT_EXPIRE' | 'EXPIRE' | 'REVOQUE' | 'INDETERMINE'
  type: string | null
  page: { id: string; name: string } | null
  scopes: string[]
  expire_le: string | null
  jours_restants: number | null
  verifie_le: string | null
  consequence: string
  action: string | null
  reconnexion_requise: boolean
}

/**
 * ÉTAT DE LA CONNEXION META — CONSÉQUENCE D'ABORD.
 *
 * L'écran affichait le message brut de Meta : « Error validating access token:
 * Session has expired on Tuesday, 25-Aug-26 13:00:00 PDT ». En anglais, daté
 * dans un fuseau américain, il ne dit ni ce qui s'est arrêté, ni quoi faire.
 *
 * On dit désormais la conséquence, puis l'action. Le code d'erreur reste dans
 * les journaux, où il sert au diagnostic — pas à l'écran, où il n'aide personne.
 */

/**
 * RECONNEXION META — LA VRAIE, PAS UN COPIER/COLLER TOUTES LES HEURES.
 *
 * Le jeton utilisé venait de l'explorateur d'API : il vit une ou deux heures.
 * Meta publie la sortie officielle — échanger ce jeton court contre un jeton
 * utilisateur longue durée, puis en dériver un jeton de Page qui n'expire pas.
 *
 * Cet écran fait les deux appels. Il demande donc l'identifiant et le secret de
 * l'application : ils partent au coffre chiffré et ne réapparaissent jamais.
 */
function ReconnexionMeta({ onFait }: { onFait: () => void }) {
  const [appId, setAppId] = useState('')
  const [secret, setSecret] = useState('')
  const [jetonCourt, setJetonCourt] = useState('')
  const [pages, setPages] = useState<{ id: string; name: string }[]>([])
  const [msg, setMsg] = useState<{ ok: boolean; texte: string } | null>(null)
  const [busy, setBusy] = useState(false)
  const [aide, setAide] = useState(false)

  const echanger = async (pageId?: string) => {
    setBusy(true)
    const r = await adminMutate<{
      ok?: boolean; message?: string; pages?: { id: string; name: string }[]; error?: string
    }>('POST', '/api/v1/market/social/instagram/token/exchange', {
      short_lived_token: jetonCourt.trim(), app_id: appId.trim(),
      ...(secret.trim() ? { app_secret: secret.trim() } : {}),
      ...(pageId ? { page_id: pageId } : {}),
    })
    setBusy(false)
    const d = r.data
    setPages(d?.pages ?? [])
    setMsg({ ok: d?.ok === true, texte: d?.message ?? 'Échange impossible.' })
    if (d?.ok) {
      // Le secret ne reste pas à l'écran une seconde de plus que nécessaire.
      setSecret(''); setJetonCourt(''); onFait()
    }
  }

  return (
    <div style={{ marginTop: 14 }}>
      <button type="button" className="lc-btn lc-btn--primary" onClick={() => setAide((v) => !v)}
        aria-expanded={aide}>
        Reconnecter Meta
      </button>

      {aide ? (
        <div style={{
          marginTop: 12, padding: 16, border: '1px solid var(--border)',
          borderRadius: 'var(--radius-card)', background: 'var(--surface-1)',
        }}>
          <p className="lc-hint" style={{ marginTop: 0 }}>
            Le jeton actuel ne durait qu’une heure ou deux parce qu’il venait de
            l’explorateur d’API. Gestion va l’échanger, par la voie officielle de
            Meta, contre un jeton de Page — celui-ci n’expire pas.
          </p>

          <ol className="lc-hint" style={{ paddingLeft: 18, lineHeight: 1.7 }}>
            <li>
              Ouvrez <strong>developers.facebook.com/apps</strong> et sélectionnez votre
              application.
            </li>
            <li>
              <strong>Paramètres → Général</strong> : recopiez l’<strong>identifiant
              de l’app</strong> et la <strong>clé secrète</strong> (bouton « Afficher »).
            </li>
            <li>
              <strong>Outils → Explorateur d’API Graph</strong> : choisissez votre app,
              puis « Générer un token d’accès » avec les autorisations
              <code> pages_show_list</code>, <code>pages_read_engagement</code> et
              <code> instagram_basic</code>.
            </li>
            <li>Collez les trois valeurs ci-dessous, puis lancez l’échange sans attendre.</li>
          </ol>

          <div className="rb-cond">
            <Field label="Identifiant de l’application" helper="Paramètres → Général → Identifiant de l’app.">
              <input className="lc-input" value={appId} onChange={(e) => setAppId(e.target.value)}
                aria-label="Identifiant de l’application Meta" autoComplete="off" />
            </Field>
            <Field label="Clé secrète de l’application"
              helper="Enregistrée au coffre chiffré. Jamais réaffichée ensuite.">
              <input className="lc-input" type="password" value={secret} autoComplete="off"
                onChange={(e) => setSecret(e.target.value)}
                placeholder="••••••••" aria-label="Clé secrète de l’application Meta" />
            </Field>
          </div>
          <Field label="Jeton d’accès de l’explorateur"
            helper="Il expire en une à deux heures : lancez l’échange juste après l’avoir copié.">
            <input className="lc-input" type="password" value={jetonCourt} autoComplete="off"
              onChange={(e) => setJetonCourt(e.target.value)}
              placeholder="Coller le jeton" aria-label="Jeton d’accès de courte durée" />
          </Field>

          {msg ? (
            <p className="lc-hint" style={{ marginTop: 10,
              color: msg.ok ? 'var(--emerald)' : 'var(--amber)' }}>{msg.texte}</p>
          ) : null}

          {/* Plusieurs Pages : c'est un choix humain. Deviner enverrait la
              veille sur le mauvais compte, et cela se verrait des semaines plus tard. */}
          {pages.length > 0 ? (
            <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {pages.map((pg) => (
                <button key={pg.id} type="button" className="lc-btn" disabled={busy}
                  onClick={() => void echanger(pg.id)}>
                  Utiliser « {pg.name} »
                </button>
              ))}
            </div>
          ) : null}

          <button type="button" className="lc-btn lc-btn--primary" style={{ marginTop: 14 }}
            disabled={busy || !appId.trim() || !jetonCourt.trim()}
            onClick={() => void echanger()}>
            {busy ? 'Échange en cours…' : 'Échanger contre un jeton durable'}
          </button>
        </div>
      ) : null}
    </div>
  )
}

function ConnexionMeta({ j, peutGerer, onVerifier }: {
  j: JetonMeta | null; peutGerer: boolean; onVerifier: () => void
}) {
  if (!j) return null
  const grave = j.etat === 'EXPIRE' || j.etat === 'REVOQUE'
  const alerte = j.etat === 'BIENTOT_EXPIRE'
  const couleur = grave ? 'var(--rose)' : alerte ? 'var(--amber)'
    : j.etat === 'VALIDE' ? 'var(--emerald)' : 'var(--text-3)'

  return (
    <section className="lc-card" style={{
      marginTop: 18, padding: 16, borderRadius: 'var(--radius-card)',
      border: `1px solid ${grave || alerte ? couleur : 'var(--border)'}`,
      background: 'var(--surface-2)',
    }}>
      <h3 className="so-class-title" style={{ marginTop: 0 }}>
        <span aria-hidden="true" style={{
          display: 'inline-block', width: 8, height: 8, borderRadius: 999,
          background: couleur, marginRight: 8,
        }} />
        Connexion Meta
      </h3>
      <p style={{ margin: '4px 0 0', color: 'var(--text-1)' }}>{j.consequence}</p>
      {j.action ? <p className="lc-hint" style={{ marginTop: 6 }}>{j.action}</p> : null}
      {/* MÉTADONNÉES SÛRES UNIQUEMENT. Le jeton lui-même n'apparaît nulle part :
          ni ici, ni dans les journaux, ni dans un rapport. */}
      <dl className="lc-kv lc-kv--blocs" style={{ marginTop: 12 }}>
        {/* « n'expire pas » promettait trop : un jeton sans échéance programmée
            reste révocable — mot de passe changé, accès retiré, application
            désactivée. On décrit ce que Meta annonce, pas une garantie qu'il
            ne donne pas. */}
        {j.type ? (
          <div><dt>Type de jeton</dt><dd>{j.type === 'PAGE'
            ? 'Jeton de Page · aucune échéance programmée'
            : j.type === 'USER' ? 'Jeton utilisateur' : j.type}</dd></div>
        ) : null}
        {j.page ? <div><dt>Page associée</dt><dd>{j.page.name}</dd></div> : null}
        <div><dt>Valide</dt><dd>{j.etat === 'VALIDE' || j.etat === 'BIENTOT_EXPIRE'
          ? 'oui' : j.etat === 'INDETERMINE' ? 'indéterminé' : 'non'}</dd></div>
        {/* Une échéance absente n'est pas une anomalie : les jetons Page n'en
            ont pas. On le dit, plutôt que d'afficher un vide inquiétant. */}
        <div><dt>Échéance</dt><dd>{j.expire_le
          ? new Date(j.expire_le).toLocaleString('fr-FR', { timeZone: 'Europe/Paris' })
          : 'Aucune échéance annoncée'}</dd></div>
        {j.jours_restants !== null && j.jours_restants > 0
          ? <div><dt>Reste</dt><dd>{j.jours_restants} jours</dd></div> : null}
        <div><dt>Vérifiée</dt><dd>{j.verifie_le
          ? new Date(j.verifie_le).toLocaleString('fr-FR', { timeZone: 'Europe/Paris' })
          : 'Jamais'}</dd></div>
        {j.scopes.length > 0 ? (
          <div style={{ gridColumn: '1 / -1' }}>
            <dt>Autorisations</dt><dd>{j.scopes.join(' · ')}</dd>
          </div>
        ) : null}
      </dl>
      {peutGerer ? (
        <>
          <button type="button" className="lc-btn" style={{ marginTop: 12 }} onClick={onVerifier}>
            Vérifier maintenant
          </button>
          <ReconnexionMeta onFait={onVerifier} />
        </>
      ) : null}
    </section>
  )
}

export function InstagramProviderPanel() {
  const permissions = usePermissions()
  const peutGerer = permissions.permissions.includes('soiree.manage')
  const toast = useToast()
  const [v, setV] = useState<ProviderView | null>(null)
  const [busy, setBusy] = useState(false)
  const [testing, setTesting] = useState(false)
  const [test, setTest] = useState<{ status: string; message: string; latency_ms: number | null } | null>(null)
  const [jeton, setJeton] = useState<JetonMeta | null>(null)

  // Champs éditables
  const [backend, setBackend] = useState<'NONE' | 'AUTHORIZED_API'>('NONE')
  const [enabled, setEnabled] = useState(false)
  const [graphVersion, setGraphVersion] = useState('v21.0')
  const [igUserId, setIgUserId] = useState('')
  const [lookback, setLookback] = useState(14)
  const [perHour, setPerHour] = useState(30)
  const [perDay, setPerDay] = useState(200)
  const [retention, setRetention] = useState(30)
  const [costCents, setCostCents] = useState(0)
  const [token, setToken] = useState('')

  const charger = () => {
    void apiGet<ProviderView>('/api/v1/market/social/instagram').then((d) => {
      setV(d)
      setBackend(d.config.backend_kind)
      setEnabled(d.config.enabled)
      setGraphVersion(d.config.backend_settings.graph_version ?? 'v21.0')
      setIgUserId(d.config.backend_settings.ig_user_id ?? '')
      setLookback(d.config.backend_settings.lookback_days ?? 14)
      setPerHour(d.config.max_requests_per_hour)
      setPerDay(d.config.max_requests_per_day)
      setRetention(d.config.retention_days)
      setCostCents(d.config.cost_per_request_millicents)
    }).catch(() => setV(null))
    void apiGet<JetonMeta>('/api/v1/market/social/instagram/token')
      .then(setJeton).catch(() => setJeton(null))
  }
  useEffect(charger, [])

  const verifierJeton = () => {
    void adminMutate<JetonMeta & { error?: string }>(
      'POST', '/api/v1/market/social/instagram/token/check')
      .then((r) => { if (r.data && !r.data.error) setJeton(r.data as JetonMeta) })
  }

  const enregistrer = async () => {
    setBusy(true)
    const r = await adminMutate<{ ok?: boolean; message?: string; error?: string }>(
      'PUT', '/api/v1/market/social/instagram', {
        backend_kind: backend, enabled,
        graph_version: graphVersion, ig_user_id: igUserId, lookback_days: lookback,
        max_requests_per_hour: perHour, max_requests_per_day: perDay,
        retention_days: retention, cost_per_request_millicents: costCents,
        ...(token.trim() ? { access_token: token.trim() } : {}),
      })
    setBusy(false)
    if (r.status === 200) {
      // Le jeton ne reste pas dans le navigateur une fois confié au coffre.
      setToken('')
      toast('ok', 'Configuration Instagram enregistrée.')
      charger()
    } else toast('error', r.data?.message ?? 'Enregistrement impossible.')
  }

  const tester = async () => {
    setTesting(true); setTest(null)
    const r = await adminMutate<{ health?: { status: string; message: string; latency_ms: number | null }; error?: string }>(
      'POST', '/api/v1/market/social/instagram/test', {})
    setTesting(false)
    if (r.status === 200 && r.data?.health) { setTest(r.data.health); charger() }
    else toast('error', 'Test impossible.')
  }

  if (!v) return null
  const actif = v.config.enabled && v.config.backend_kind !== 'NONE'

  return (
    <section className="lc-panel gd-panel" aria-labelledby="set-instagram">
      <header className="lc-panel__head">
        <h2 className="lc-section-title" id="set-instagram">
          <AtSign size={16} aria-hidden="true" /> Instagram — veille
        </h2>
        <span className={`ig-pill ig-pill--${actif ? v.config.status.toLowerCase() : 'off'}`}>
          {actif ? STATUS_LABEL[v.config.status] ?? v.config.status : 'Non configuré'}
        </span>
      </header>
      <div className="lc-panel__body">
        <p className="lc-hint">
          Les établissements surveillés ont souvent un compte Instagram. Sans fournisseur
          configuré, Gestion l'affiche comme référence — et ne prétend pas le lire.
        </p>

        {/* ─── §19 : la matrice, telle qu'elle est ─── */}
        <h3 className="so-class-title">Ce que ce fournisseur permet réellement</h3>
        <ul className="ig-caps">
          {CAP_ORDER.map((k) => {
            const state = v.capabilities[k] ?? 'UNKNOWN'
            return (
              <li key={k} className={`ig-cap ig-cap--${state.toLowerCase()}`}>
                <span className="ig-cap__name">{CAP_LABEL[k]}</span>
                <span className="ig-cap__state">{CAP_TEXT[state]}</span>
                {v.notes[k] ? <span className="lc-hint ig-cap__note">{v.notes[k]}</span> : null}
              </li>
            )
          })}
        </ul>

        <h3 className="so-class-title" style={{ marginTop: 18 }}>Fournisseur</h3>
        <Field label="Voie de collecte"
          helper="« Aucun » est un état légitime : le compte reste référencé, la couverture est annoncée partielle.">
          <Select value={backend} options={BACKEND_OPTIONS} ariaLabel="Voie de collecte"
            onChange={(x) => setBackend(x as 'NONE' | 'AUTHORIZED_API')} />
        </Field>

        {backend === 'AUTHORIZED_API' ? (
          <>
            <div className="vw-grid">
              <Field label="Version du graphe">
                <TextInput value={graphVersion} onChange={setGraphVersion} maxLength={12}
                  ariaLabel="Version du graphe" disabled={!peutGerer} />
              </Field>
              <Field label="Identifiant du compte interrogateur"
                helper="Notre propre compte professionnel — c'est lui qui interroge.">
                <TextInput value={igUserId} onChange={setIgUserId} maxLength={40}
                  ariaLabel="Identifiant du compte interrogateur" disabled={!peutGerer} />
              </Field>
              <Field label="Fenêtre de veille (jours)"
                helper="Au-delà, Gestion ne remonte pas : la veille regarde devant.">
                <NumberInput value={lookback} onChange={(x) => setLookback(Number(x) || 14)}
                  min={1} max={90} unit="j" ariaLabel="Fenêtre de veille" />
              </Field>
            </div>
            <Field label="Jeton d'accès"
              helper={v.config.has_credentials
                ? 'Un jeton est enregistré au coffre. Laissez vide pour le conserver.'
                : 'Confié au coffre chiffré, jamais réaffiché ni journalisé.'}>
              <TextInput value={token} onChange={setToken} maxLength={500}
                placeholder={v.config.has_credentials ? '•••••••• (inchangé)' : 'Coller le jeton'}
                ariaLabel="Jeton d'accès" disabled={!peutGerer} />
            </Field>
          </>
        ) : null}

        <SwitchRow checked={enabled} onChange={setEnabled} disabled={!peutGerer || backend === 'NONE'}
          title="Collecte activée"
          desc="Rien n'est collecté tant que ce commutateur est fermé, même avec un jeton valide." />

        <h3 className="so-class-title" style={{ marginTop: 18 }}>Budget, rétention et coût</h3>
        <div className="vw-grid">
          <Field label="Appels / heure" helper="S'ajoute au budget global de la veille.">
            <NumberInput value={perHour} onChange={(x) => setPerHour(Number(x) || 30)} min={1} max={1000} ariaLabel="Appels par heure" />
          </Field>
          <Field label="Appels / jour">
            <NumberInput value={perDay} onChange={(x) => setPerDay(Number(x) || 200)} min={1} max={10000} ariaLabel="Appels par jour" />
          </Field>
          <Field label="Rétention des contenus (jours)"
            helper="Le signal extrait survit à la purge : effacer la trace n'efface pas ce qu'on en a compris.">
            <NumberInput value={retention} onChange={(x) => setRetention(Number(x) || 30)} min={1} max={365} unit="j" ariaLabel="Rétention" />
          </Field>
          <Field label="Coût par appel (millicentimes)"
            helper="Zéro pour l'API officielle. À renseigner si un fournisseur facturé est branché.">
            <NumberInput value={costCents} onChange={(x) => setCostCents(Number(x) || 0)} min={0} max={1000000} ariaLabel="Coût par appel" />
          </Field>
        </div>

        <h3 className="so-class-title" style={{ marginTop: 18 }}>Consommation</h3>
        <dl className="lc-kv">
          <div><dt>Appels (1 h / 24 h)</dt><dd>{v.usage.requests_hour} / {v.usage.requests_day}</dd></div>
          <div><dt>Appels (30 j)</dt><dd>{v.usage.requests_month}</dd></div>
          <div><dt>Contenus récoltés (30 j)</dt><dd>{v.usage.items_month}</dd></div>
          <div><dt>Erreurs (24 h)</dt><dd>{v.usage.errors_day}</dd></div>
          <div><dt>Coût (30 j)</dt><dd>{euros(v.usage.cost_month_millicents)}</dd></div>
          <div><dt>Dernière vérification</dt><dd>{quandFr(v.config.last_check_at)}</dd></div>
        </dl>
        {/* Le message brut de Meta n'est plus rendu ici : il vit dans les
            journaux. Ce qui compte à l'écran est dit par ConnexionMeta. */}
        <ConnexionMeta j={jeton} peutGerer={peutGerer} onVerifier={verifierJeton} />

        {peutGerer ? (
          <div className="vw-foot__btns" style={{ marginTop: 16 }}>
            <button type="button" className="lc-btn" disabled={testing || backend === 'NONE'}
              onClick={() => void tester()}>
              {testing
                ? <><Loader2 size={14} className="vw-spin" /> Test en cours…</>
                : <><Activity size={14} /> Tester la connexion</>}
            </button>
            <button type="button" className="lc-btn lc-btn--primary" disabled={busy}
              onClick={() => void enregistrer()}>
              {busy ? <><Loader2 size={14} className="vw-spin" /> Enregistrement…</>
                : <><ShieldCheck size={14} /> Enregistrer</>}
            </button>
          </div>
        ) : null}

        {test ? (
          <p className={`ig-test ig-test--${test.status.toLowerCase()}`} role="status">
            {STATUS_LABEL[test.status] ?? test.status} — {test.message}
            {test.latency_ms !== null ? ` (${test.latency_ms} ms)` : ''}
          </p>
        ) : null}

        <StoriesProviderPanel />
      </div>
    </section>
  )
}
